import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ShieldAlert } from "lucide-react";

function fmtTime(d: any) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function actionBadge(action: string) {
  const dangerous = action.includes("delete") || action.includes("suspend") || action.includes("impersonate");
  return (
    <Badge variant={dangerous ? "destructive" : "secondary"} className="text-[10px] font-mono">
      {action}
    </Badge>
  );
}

export function AuditTab() {
  const [targetType, setTargetType] = useState<"all" | "tenant" | "plan" | "user" | "system">("all");
  const [actionFilter, setActionFilter] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const { data, isLoading } = trpc.superAdmin.auditLogs.useQuery({
    targetType: targetType === "all" ? undefined : targetType,
    action: actionFilter || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-500" />
          Auditoria
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Todas as ações administrativas executadas por SuperAdmins.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={targetType} onValueChange={(v: any) => { setTargetType(v); setPage(0); }}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="tenant">Empresas</SelectItem>
            <SelectItem value="plan">Planos</SelectItem>
            <SelectItem value="user">Usuários</SelectItem>
            <SelectItem value="system">Sistema</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Filtrar por ação…" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} className="max-w-xs" />
        <span className="text-xs text-muted-foreground ml-auto">{total} ação/ões registrada(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhuma ação registrada ainda.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Quando</TableHead>
                  <TableHead>Quem</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Alvo</TableHead>
                  <TableHead>Mudanças</TableHead>
                  <TableHead className="w-[120px]">IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtTime(r.createdAt)}</TableCell>
                    <TableCell className="text-sm">{r.actorEmail}</TableCell>
                    <TableCell>{actionBadge(r.action)}</TableCell>
                    <TableCell className="text-sm">
                      <Badge variant="outline" className="text-[10px] mr-1">{r.targetType}</Badge>
                      {r.targetLabel ?? (r.targetId ? `#${r.targetId}` : "—")}
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      {r.changes ? (
                        <details>
                          <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">ver</summary>
                          <pre className="text-[10px] bg-muted/50 p-2 mt-1 rounded overflow-auto max-h-[200px]">{JSON.stringify(r.changes, null, 2)}</pre>
                        </details>
                      ) : <span className="text-[11px] text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-[11px] text-muted-foreground font-mono">{r.ip ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-xs text-muted-foreground">{page + 1} de {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Próximo</Button>
        </div>
      )}
    </div>
  );
}
